import { useCallback, useEffect, useMemo, useState, type ReactNode } from 'react';

import { toAppError, type AppError } from '@/infrastructure/errors/AppError';
import {
  observeIdToken,
  resolveRole,
  signInWithGoogle,
  signOut as firebaseSignOut,
} from '@/infrastructure/firebase/authService';
import { upsertUserProfile } from '@/infrastructure/repositories/userRepository';

import { AuthContext, type AuthContextValue, type AuthSession } from './authContext';

interface AuthProviderProps {
  children: ReactNode;
}

export function AuthProvider({ children }: AuthProviderProps) {
  const [session, setSession] = useState<AuthSession>({ status: 'loading' });
  const [error, setError] = useState<AppError | null>(null);

  useEffect(() => {
    let active = true;

    const unsubscribe = observeIdToken((user) => {
      if (user === null) {
        if (active) {
          setSession({ status: 'anonymous' });
        }
        return;
      }

      void (async () => {
        try {
          const role = await resolveRole(user);
          if (!active) {
            return;
          }

          setSession({
            status: 'authenticated',
            account: {
              uid: user.uid,
              displayName: user.displayName,
              email: user.email,
              photoUrl: user.photoURL,
              role,
            },
          });

          // The profile mirror is convenience, not a precondition for using
          // the app: a failure here must not lock the captain out.
          await upsertUserProfile(user);
        } catch (caught) {
          if (active) {
            setError(toAppError(caught));
          }
        }
      })();
    });

    return () => {
      active = false;
      unsubscribe();
    };
  }, []);

  const signIn = useCallback(async () => {
    setError(null);
    try {
      await signInWithGoogle();
    } catch (caught) {
      const appError = toAppError(caught);
      // Closing the popup is a decision, not a failure worth reporting.
      if (appError.kind !== 'sign-in-cancelled') {
        setError(appError);
      }
    }
  }, []);

  const signOut = useCallback(async () => {
    setError(null);
    try {
      await firebaseSignOut();
    } catch (caught) {
      setError(toAppError(caught));
    }
  }, []);

  const dismissError = useCallback(() => {
    setError(null);
  }, []);

  const value = useMemo<AuthContextValue>(
    () => ({ session, error, signIn, signOut, dismissError }),
    [session, error, signIn, signOut, dismissError],
  );

  return <AuthContext value={value}>{children}</AuthContext>;
}
